/**
 * CORP//LLM — Centralized API Client
 * All server HTTP calls go through this module.
 * Handles auth headers, error handling, and typed responses.
 */

import { getJwt } from './auth';

// ── Server Base URL ─────────────────────────────────────────────────────────

let _serverBase = '';

export function setServerBase(base: string): void { _serverBase = base; }
export function getServerBase(): string { return _serverBase; }

// ── Core fetch wrapper ──────────────────────────────────────────────────────

/**
 * Extract a human-readable message from an HTTP error body. Supports both
 * RFC 7807 Problem Details (`{detail: "..."}`) — the default Huma shape the
 * server migrated to — and the legacy CORP//LLM envelope (`{error: "..."}`)
 * for any endpoint that hasn't been migrated yet. Falls back to the raw body
 * text when neither shape matches.
 */
function extractErrorDetail(status: number, body: string): string {
  if (!body) return `HTTP ${status}`;
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed && typeof parsed === 'object') {
      const p = parsed as Record<string, unknown>;
      if (typeof p.detail === 'string' && p.detail) return p.detail;
      if (typeof p.error === 'string' && p.error) return p.error;
    }
  } catch {
    /* body is not JSON — fall through */
  }
  return body;
}

export class ApiError extends Error {
  /** Parsed human-readable detail. Prefers RFC 7807 `detail`, falls back to legacy `error`, then raw body. */
  public readonly detail: string;

  constructor(public status: number, public body: string) {
    const detail = extractErrorDetail(status, body);
    super(`API ${status}: ${detail}`);
    this.name = 'ApiError';
    this.detail = detail;
  }
}

interface FetchOptions {
  method?: string;
  body?: unknown;
  /** Skip Authorization header (e.g. for public endpoints). */
  noAuth?: boolean;
}

/**
 * Typed fetch wrapper. Adds Authorization header from localStorage JWT,
 * serializes JSON body, and parses JSON response.
 */
export async function fetchAPI<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = 'GET', body, noAuth = false } = opts;
  const headers: Record<string, string> = {};

  if (!noAuth) {
    const jwt = getJwt();
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
  }

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${_serverBase}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ── Typed API methods ───────────────────────────────────────────────────────

// -- Profile --

export interface PlayerProfile {
  id: string;
  email: string;
  name: string;
  avatarUrl: string;
  createdAt: string;
  isAdmin: boolean;
  sessions: PlayerSession[];
}

export interface PlayerSession {
  sessionId: string;
  name: string;
  status: string;
  corpName: string;
  day: number;
  maxPlayers: number;
}

export function getProfile(): Promise<PlayerProfile> {
  return fetchAPI('/api/players/me');
}

// -- Providers --

export interface ProvidersResponse {
  google: boolean;
  github: boolean;
  discord: boolean;
}

export function getProviders(): Promise<ProvidersResponse> {
  return fetchAPI('/api/players/me/providers');
}

// -- Preferences --

export interface Preferences {
  theme: string;
  accentColor: string;
  language: string;
  notifCrit: boolean;
  notifWarn: boolean;
  notifInfo: boolean;
  // Character customization
  characterId: string;
  skinTone: string;
  pose: string;
  attachments: Record<string, string>;
  portraitData: string;
  // client#240 — first-session guidance permanently dismissed (server-persisted)
  onboardingDismissed: boolean;
}

export function getPreferences(): Promise<Preferences> {
  return fetchAPI('/api/players/me/preferences');
}

export function patchPreferences(patch: Partial<Preferences>): Promise<{ status: string }> {
  return fetchAPI('/api/players/me/preferences', { method: 'PATCH', body: patch });
}

// -- Client error reporting (#751 Ops-Härtung Teil 3) --

export interface ClientErrorReport {
  message: string;
  stack?: string;
  screen?: string;
  clientVersion?: string;
  source?: string;
}

/** Fire-and-forget sink for unhandled errors — see utils/error-reporter.ts. */
export function reportClientError(rep: ClientErrorReport): Promise<{ status: string }> {
  return fetchAPI('/api/client-errors', { method: 'POST', body: rep });
}

// -- Plan Submit --

export interface PlanSubmitRequest {
  hood_id: string;
  actions: PlanSubmitAction[];
}

export interface PlanSubmitAction {
  runner?: string;
  team?: string;
  command: string;
  target?: string;
  condition?: unknown;
  fallback?: unknown;
  /** Buildings list for ROUTE-style actions — first entry is also the
   *  primary target_node_id used by the engine (see planconv). For
   *  INSTALL_CYBERWARE this holds the ripperdoc's nodeId. */
  buildings?: string[];
  /** HP system Phase 4 (#589) — INSTALL_CYBERWARE submission params.
   *  cyberwareId matches an entry in data/cyberware.json; targetPart
   *  is the body-part enum ("head" | "torso" | "arm_left" | ...).
   *  Both omitted for non-cyberware actions. */
  cyberwareId?: string;
  targetPart?: string;
  /** Target-Polymorphie (#211) — verb×target-kind selector from the plan-UI
   *  picker. targetKind names the kind (e.g. "auditor"); planconv on the
   *  server maps verb+kind to the concrete dispatch. targetRef is reserved
   *  for a specific-entity id (deferred). Both omitted for generic targets. */
  targetKind?: string;
  targetRef?: string;
}

export interface PlanSubmitResponse {
  ok: boolean;
  planId: string;
  errors: string[];
}

export function submitPlan(sessionId: string, plan: PlanSubmitRequest): Promise<PlanSubmitResponse> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/submit_plan`, {
    method: 'POST',
    body: plan,
  });
}

// -- Plan Validate --

export interface ValidateAction {
  runner: string;
  action: string;
  sector: string;
  target?: string;
}

export interface ValidateIssue {
  action_index: number;
  code: string;
  message: string;
}

export interface ValidatePlanResponse {
  valid: boolean;
  errors: ValidateIssue[];
  warnings: ValidateIssue[];
}

export function validatePlan(sessionId: string, actions: ValidateAction[]): Promise<ValidatePlanResponse> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/validate_plan`, {
    method: 'POST',
    body: { actions },
  });
}

// -- Resolve Tick --

export interface ResolveTickResponse {
  ok: boolean;
  day: number;
  gameOver: boolean;
  winner?: string;
  error?: string;
}

export function resolveTick(sessionId: string): Promise<ResolveTickResponse> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/resolve_tick`, {
    method: 'POST',
  });
}

// -- Async Tick Job (#228 Phase 4) --

export type TickJobStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface EnqueueTickResponse {
  jobId: string;
  day: number;
  status: TickJobStatus;
  estimatedDurationMs: number;
}

export interface TickJob {
  id: string;
  sessionId: string;
  day: number;
  status: TickJobStatus;
  progressPhase: string;
  progressPct: number;
  triggeredBy: string;
  enqueuedAt: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
}

/**
 * Enqueue an async tick-resolution job. Returns 202 + jobId; client should
 * subscribe to WS tick_progress messages keyed by jobId or poll
 * getTickJob(jobId) as fallback. Replaces the synchronous resolveTick path
 * for callers that can handle long ticks (NPC-Population multi-min ticks
 * per #227).
 */
export function resolveTickAsync(sessionId: string): Promise<EnqueueTickResponse> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/resolve_tick_async`, {
    method: 'POST',
  });
}

/** Polling fallback when WS channel is unavailable. */
export function getTickJob(jobId: string): Promise<TickJob> {
  return fetchAPI(`/api/tick_jobs/${encodeURIComponent(jobId)}`);
}

/** Recent jobs for a session — debug / admin observability. */
export function listTickJobs(sessionId: string, limit = 20): Promise<{ jobs: TickJob[] }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/tick_jobs?limit=${limit}`);
}

// -- Player Ready --

export interface ReadyResponse {
  status: string;
  readyCount: number;
  totalPlayers: number;
}

export function markReady(sessionId: string): Promise<ReadyResponse> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/ready`, {
    method: 'POST',
  });
}

// -- Start Session (creator only, bypasses lobby timer) --

export function startSession(sessionId: string): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/start`, {
    method: 'POST',
  });
}

// -- Leave Session --

export function leaveSession(sessionId: string): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/leave`, {
    method: 'DELETE',
  });
}

// -- Chat --

export function sendChat(sessionId: string, message: string): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/chat`, {
    method: 'POST',
    body: { message },
  });
}

/** History entry returned by GET /api/sessions/{id}/chat.
 *  Subset of ChatMessage (no sessionId on the row — it's implicit). */
export interface ChatHistoryEntry {
  playerId: string;
  playerName: string;
  corpName: string;
  message: string;
  timestamp: string;
}

/** v1.8.2+: persisted chat history for a session. Server returns oldest-first.
 *  Older servers 404 this endpoint — caller should treat that as empty. */
export function getChatHistory(sessionId: string, limit = 100): Promise<{ messages: ChatHistoryEntry[] }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/chat?limit=${limit}`);
}

// -- Chat WS event payload (for typing, not an HTTP call) --

export interface ChatMessage {
  sessionId: string;
  playerId: string;
  playerName: string;
  corpName: string;
  message: string;
  timestamp: string;
}

// -- Runners --

export interface HireRunnerRequest {
  candidateIdx: number;
}

export function hireRunner(sessionId: string, req: HireRunnerRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/runners/hire`, {
    method: 'POST',
    body: req,
  });
}

export interface AssignManagerRequest {
  runnerId: string;
  role: string;
  buildingId?: string;
}

export function assignManager(sessionId: string, req: AssignManagerRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/runners/assign`, {
    method: 'POST',
    body: req,
  });
}

// -- Contacts --

export interface ScoutContactRequest {
  contactId: string;
}

export function scoutContact(sessionId: string, req: ScoutContactRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/contacts/scout`, {
    method: 'POST',
    body: req,
  });
}

export function reconSector(sessionId: string): Promise<{ status: string; sectorId?: string; error?: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/sectors/recon`, {
    method: 'POST',
    body: {},
  });
}

// ── Team Management (Spec §9) ──────────────────────────────────────

export function createTeam(sessionId: string, name: string): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/teams`, {
    method: 'POST',
    body: { name },
  });
}

export function deleteTeam(sessionId: string, teamIdx: number): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/teams/${teamIdx}`, {
    method: 'DELETE',
  });
}

export function assignToTeam(sessionId: string, teamIdx: number, runnerIdx: number): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/teams/${teamIdx}/assign`, {
    method: 'POST',
    body: { runnerIdx },
  });
}

export function unassignFromTeam(sessionId: string, teamIdx: number, runnerIdx: number): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/teams/${teamIdx}/unassign`, {
    method: 'POST',
    body: { runnerIdx },
  });
}

export function setTeamLeader(sessionId: string, teamIdx: number, runnerIdx: number): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/teams/${teamIdx}/leader`, {
    method: 'POST',
    body: { runnerIdx },
  });
}

export interface BribeContactRequest {
  contactId: string;
  amount: number;
}

export function bribeContact(sessionId: string, req: BribeContactRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/contacts/bribe`, {
    method: 'POST',
    body: req,
  });
}

export interface SupportCampaignRequest {
  campaignId: string;
}

export function supportCampaign(sessionId: string, req: SupportCampaignRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/campaigns/support`, {
    method: 'POST',
    body: req,
  });
}

// ── M4a Contact Drama Arcs ─────────────────────────────────────────────────

export interface ChainReferralRequest {
  contactId: string;
}

/** Attempt a CHAIN_REFERRAL on a long-term payroll contact. Returns `status: "ok"` on
 * success, `status: "failed"` when the referral roll missed (loyalty still deducted). */
export function chainReferral(sessionId: string, req: ChainReferralRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/contacts/chain`, {
    method: 'POST',
    body: req,
  });
}

export interface PoachBidRequest {
  victimPlayerIdx: number;
  contactId: string;
  amount: number;
}

/** Submit an anonymous POACH bid against a rival's contact. Opens a 2-day bid-war. */
export function poachBid(sessionId: string, req: PoachBidRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/contacts/poach`, {
    method: 'POST',
    body: req,
  });
}

export interface CounterBidRequest {
  contactId: string;
  amount: number;
}

/** Counter an incoming POACH with a retention bid. Resolution at EOD2. */
export function counterBid(sessionId: string, req: CounterBidRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/contacts/counter`, {
    method: 'POST',
    body: req,
  });
}

// ── M4b-redux Criminal Justice System ──────────────────────────────────────

export interface RescueRunnerRequest {
  arrestedRunnerId: string;
  rescuerRunnerId: string;
}

/** Attempt a stealth rescue of an arrested runner using STL+CHA vs severity DC.
 *  Outcomes: "success" | "partial_fail" | "critical_fail". Prep cost always consumed. */
export function rescueRunner(sessionId: string, req: RescueRunnerRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/runners/rescue`, {
    method: 'POST',
    body: req,
  });
}

export interface BribeReleaseRequest {
  arrestedRunnerId: string;
}

/** Pay a Judge contact to release an arrested runner. Rejects federal severity
 *  with 400 FEDERAL_NOT_BRIBABLE. Requires Arbitration Tribunal HIGH on payroll. */
export function bribeRelease(sessionId: string, req: BribeReleaseRequest): Promise<{ status: string }> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/runners/bribe-release`, {
    method: 'POST',
    body: req,
  });
}

// ── Phase 3 M1 Crisis Events ───────────────────────────────────────────────

export interface CrisisRespondRequest {
  actionId: string;
}

/** Submit a counter-action against an active crisis. Available actionIds:
 *  bribe_journalist, legal_defense, strike_busting, community_donation.
 *  Must be in the crisis's counter_actions list. Deducts cash immediately;
 *  effect mitigation applies at the crisis deadline. */
export function crisisRespond(
  sessionId: string,
  crisisId: string,
  req: CrisisRespondRequest,
): Promise<{ status: string }> {
  return fetchAPI(
    `/api/sessions/${encodeURIComponent(sessionId)}/crises/${encodeURIComponent(crisisId)}/respond`,
    { method: 'POST', body: req },
  );
}

// -- Bot Corps --

export interface BotCorp {
  name: string;
  temperament: string;
  // Narrative fields (added in server v1.1.0 alongside SPEC_bot_personalities.md).
  // Optional on the client so older server versions or fixtures without the
  // new fields keep rendering — the lobby and epitaph modal degrade to
  // name+temperament only.
  key?: string;
  voice_style?: string;
  signature_action?: string;
}

export function getBotCorps(): Promise<BotCorp[]> {
  return fetchAPI('/api/botcorps', { noAuth: true });
}

// -- Session Detail --

export interface SessionDetailPlayer {
  playerId: string;
  corpName: string;
  accentColor: string;
  joinedAt: string;
  /** Ready flag persists across reloads — server v1.8.2+. Older servers omit it. */
  ready?: boolean;
  /** True when the row is a bot (server v1.8.2+). */
  isBot?: boolean;
}

export interface SessionDetail {
  id: string;
  name: string;
  status: string;
  maxPlayers: number;
  seed: number;
  day: number;
  mode: string;
  createdBy: string;
  createdAt: string;
  players: SessionDetailPlayer[];
  deadlineHour: number;
  minLobbyUntil?: string;
}

export function getSessionDetail(sessionId: string): Promise<SessionDetail> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}`);
}

// -- Admin --

export interface AdminPlayer {
  id: string;
  email: string;
  name: string;
  avatarUrl: string;
  isAdmin: boolean;
  isBetaUser: boolean;
  /** True for bot-service spawned accounts (is_bot column). Added server v1.6.2+. */
  isBot?: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  lastActivityAt?: string;
  /** Session the player / bot is currently joined to (lobby or running).
   *  Server v1.8.3+. Empty / undefined when not in any active session. */
  activeSessionId?: string;
  activeSessionName?: string;
  activeSessionStatus?: string;
}

export interface AdminPlayerSession {
  sessionId: string;
  sessionName: string;
  corpName: string;
  accentColor: string;
  status: string;
  day: number;
  joinedAt: string;
}

export interface AdminSession {
  id: string;
  name: string;
  status: string;
  maxPlayers: number;
  playerCount: number;
  day: number;
  mode: string;
  createdBy: string;
  createdAt: string;
  winner?: string;
  lastTickAt?: string;
  /** v1.9.0 (specs#32): admin-session test-harness flag. Admin panel
   *  filters on this to separate them into their own tab. */
  isAdminSession?: boolean;
}

/** Body accepted by POST /api/admin/sessions — mirrors the public
 *  /api/sessions body but lands in the admin-only endpoint which forces
 *  is_admin_session=true server-side and bypasses the session-limit check. */
export interface AdminCreateSessionBody {
  name: string;
  corpName: string;
  seed?: number;
  maxPlayers?: number;
  mode?: 'quick' | 'standard' | 'epic';
  accentColor?: string;
  deadlineHour?: number;
  bots?: Array<{ corpName: string; temperament: string; difficulty: string; strategy: string }>;
  password?: string;
}

export interface AdminCreateSessionResult {
  sessionId: string;
  seed: number;
  deadlineHour?: number;
  effectivePlayers?: number;
  citySize?: number;
}

export function createAdminSession(body: AdminCreateSessionBody): Promise<AdminCreateSessionResult> {
  return fetchAPI('/api/admin/sessions', { method: 'POST', body });
}

export function deleteAdminSession(id: string): Promise<{ status: string }> {
  return fetchAPI(`/api/admin/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Body for POST /api/admin/sessions/{id}/spawn-bots (server v1.9.1+).
 *  Adds one or more bots to a running admin session. Fails 403 if the
 *  target is not an admin session. */
export interface AdminSpawnBotsBody {
  bots: Array<{
    corpName: string;
    strategy: string;
    temperament?: string;
    difficulty?: string;
  }>;
}

export function spawnBotsAdmin(sessionId: string, body: AdminSpawnBotsBody): Promise<{ status: string; spawned: string[] }> {
  return fetchAPI(`/api/admin/sessions/${encodeURIComponent(sessionId)}/spawn-bots`, {
    method: 'POST',
    body,
  });
}

export function getAdminPlayers(): Promise<AdminPlayer[]> {
  return fetchAPI('/api/admin/players');
}

export function patchAdminPlayer(id: string, patch: { isBetaUser?: boolean }): Promise<{ status: string }> {
  return fetchAPI(`/api/admin/players/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export function deleteAdminPlayer(id: string): Promise<{ status: string }> {
  return fetchAPI(`/api/admin/players/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function getAdminSessions(): Promise<AdminSession[]> {
  return fetchAPI('/api/admin/sessions');
}

export function getAdminOnline(): Promise<Record<string, string>> {
  return fetchAPI('/api/admin/online');
}

export function getAdminPlayerSessions(playerId: string): Promise<AdminPlayerSession[]> {
  return fetchAPI(`/api/admin/players/${encodeURIComponent(playerId)}/sessions`);
}

// --- Admin Config ---

export function getAdminActions(): Promise<unknown[]> {
  return fetchAPI('/api/admin/actions');
}

export function putAdminActions(actions: unknown[]): Promise<{ ok: boolean; sha: string; count: number; commit?: string }> {
  return fetchAPI('/api/admin/actions', { method: 'PUT', body: actions });
}

export function getAdminParams(): Promise<unknown> {
  return fetchAPI('/api/admin/params');
}

export function putAdminParams(params: unknown): Promise<{ ok: boolean; sha: string; changed_keys: string[]; commit?: string }> {
  return fetchAPI('/api/admin/params', { method: 'PUT', body: params });
}

// ── Player update system (notifications / motd / discord) ─────────────────

export type NotificationSeverity = 'info' | 'warn' | 'crit';

export interface Notification {
  id: string;
  severity: NotificationSeverity;
  title: string;
  body: string;
  linkUrl?: string;
  linkLabel?: string;
  createdAt: string;
  isGlobal: boolean;
  read: boolean;
}

export interface NotificationList {
  notifications: Notification[];
  unreadCount: number;
}

export function listNotifications(): Promise<NotificationList> {
  return fetchAPI('/api/players/me/notifications');
}

export function markNotificationRead(id: string): Promise<{ status: string }> {
  return fetchAPI(`/api/players/me/notifications/${encodeURIComponent(id)}/read`, { method: 'POST' });
}

export function markAllNotificationsRead(): Promise<{ marked: number }> {
  return fetchAPI('/api/players/me/notifications/read_all', { method: 'POST' });
}

export type NotificationTarget = 'global' | 'beta' | 'players';

export interface NotificationCreateBody {
  target: NotificationTarget;
  playerIds?: string[];
  severity?: NotificationSeverity;
  title: string;
  body: string;
  linkUrl?: string;
  linkLabel?: string;
  expiresAt?: string;
}

export function adminCreateNotification(body: NotificationCreateBody): Promise<{ recipients: number; global: boolean }> {
  return fetchAPI('/api/admin/notifications', { method: 'POST', body });
}

export function adminDeleteNotification(id: string): Promise<{ status: string }> {
  return fetchAPI(`/api/admin/notifications/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface DiscordBroadcastBody {
  title: string;
  body: string;
  severity?: NotificationSeverity;
}

export function adminBroadcastDiscord(body: DiscordBroadcastBody): Promise<{ status: string }> {
  return fetchAPI('/api/admin/broadcast/discord', { method: 'POST', body });
}

export interface Motd {
  text: string;
  severity: NotificationSeverity;
  expiresAt?: string;
  updatedAt?: string;
}

export function getMotd(): Promise<Motd> {
  return fetchAPI('/api/motd', { noAuth: true });
}

export interface AdminMotdPutBody {
  text: string;
  severity?: NotificationSeverity;
  expiresAt?: string;
}

export function adminPutMotd(body: AdminMotdPutBody): Promise<{ status: string }> {
  return fetchAPI('/api/admin/motd', { method: 'PUT', body });
}

// ── Economy — Phase 3 (history + peer aggregates, server v1.10.2+) ─────────

export interface EconomySnapshot {
  day: number;
  legalIncome: number;
  dirtyIncome: number;
  fixedExpenses: number;
  actionExpenses: number;
  net: number;
  cleanCash: number;
  dirtyCash: number;
  /** Per-registry-key snapshot values — server v1.10.4+. Missing entry
   *  treated as zero. Drives per-row mini-sparklines in the Tages-Bilanz. */
  byCategory?: Record<string, number>;
}

export interface EconomyHistoryResponse {
  snapshots: EconomySnapshot[];
}

/** Ring-buffered per-cycle P&L snapshots for the calling player. Used by
 *  the economy screen for sparklines + vs-previous-cycle diffs. */
export function getEconomyHistory(sessionId: string): Promise<EconomyHistoryResponse> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/economy/history`);
}

export interface EconomyPeersResponse {
  self: EconomySnapshot;
  median: EconomySnapshot;
  top25: EconomySnapshot;
}

/** Anonymised per-field peer aggregates for the current cycle. Drives the
 *  optional "Vergleich: Median / Top 25%" overlay on the overview tab. */
export function getEconomyPeers(sessionId: string): Promise<EconomyPeersResponse> {
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/economy/peers`);
}

// ── Economy Forecast — Phase 4 (server v1.10.3+) ──────────────────────────

export interface ForecastAssumption {
  key: string;
  label: string;
  value: number;
}

export interface ForecastModel {
  days: number;
  expectedIncome: number;
  expectedExpenses: number;
  expectedNet: number;
  projectedCleanEnd: number;
  assumptions: ForecastAssumption[];
}

/** Bottom-up forecast combining deterministic passive cashflow with a
 *  rolling average of recent action-driven activity. Client renders the
 *  Assumption list as a "why this number" tooltip on the forecast panel. */
export function getEconomyForecast(sessionId: string, days = 7): Promise<ForecastModel> {
  const q = days && days !== 7 ? `?days=${days}` : '';
  return fetchAPI(`/api/sessions/${encodeURIComponent(sessionId)}/economy/forecast${q}`);
}

